/**
 * Grade scale bar: 0点 → 満点 segmented by grade (E〜A),
 * with a flag marker on the 合格基準 (passing line) position.
 */
import { Box, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import FlagIcon from "@mui/icons-material/Flag";
import { IconArrowShapeUp } from "@/assets/icons";
import { text, primary } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded, spacing } from "@/theme/variables";
import { GRADE_COLORS } from "@/features/mock-exam/components/grade-guide/grade-guide.constants";
import { getGradeScaleSegments } from "@/features/mock-exam/components/grade-guide/grade-scale.utils";

const EDGE_BAND_PERCENT = 20;
const EDGE_NUDGE_PERCENT = 10;

/**
 * Horizontal shift for a marker label at `positionPercent` of the bar: centered on its marker,
 * but pinned to the nearby edge inside the outer bands so long labels stay inside the card.
 */
const getMarkerLabelShift = (positionPercent: number): string => {
  const position = Math.min(100, Math.max(0, positionPercent));

  if (position < EDGE_BAND_PERCENT) return `translateX(-${position + EDGE_NUDGE_PERCENT}%)`;
  if (position > 100 - EDGE_BAND_PERCENT) return `translateX(-${position - EDGE_NUDGE_PERCENT}%)`;

  return "translateX(-50%)";
};

type TMockExamGradeScaleBarProps = {
  /** 合格基準 as a share of 満点, e.g. 70 → 「合格基準: 70%」 */
  passingScorePercentage: number;
  /** Position (%) of the attempt result — omit to hide the 模試の結果 marker (exam not taken yet). */
  resultPositionPercent?: number;
};

export const MockExamGradeScaleBar: FC<TMockExamGradeScaleBarProps> = ({ passingScorePercentage, resultPositionPercent }) => {
  const { t } = useTranslation();

  // Sized by the real share of 満点, so the flag and the B/A boundary always line up.
  const segments = getGradeScaleSegments(passingScorePercentage);

  return (
    <Stack gap={spacing.xxs}>
      {/* 合格基準 label */}
      <Box sx={{ position: "relative", height: 18 }}>
        <Typography
          noWrap
          sx={{
            ...typography.cap12Bold,
            color: primary[500],
            position: "absolute",
            left: `${passingScorePercentage}%`,
            maxWidth: "100%",
            transform: getMarkerLabelShift(passingScorePercentage),
          }}
        >
          {t("mockExam.grade.guide.passingLine", { percent: passingScorePercentage })}
        </Typography>
      </Box>

      {/* Scale endpoints + marker flag */}
      <Box sx={{ position: "relative", display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ ...typography.cap12, color: text.secondary }}>{t("mockExam.grade.guide.zeroPoint")}</Typography>
        <FlagIcon
          sx={{
            fontSize: 14,
            color: primary[500],
            position: "absolute",
            left: `${passingScorePercentage}%`,
            bottom: 0,
            // Pole sits at x≈6 of the 24px viewBox — shift a quarter width to plant it on the B/A boundary.
            transform: "translateX(-25%)",
          }}
        />
        <Typography sx={{ ...typography.cap12, color: text.secondary }}>{t("mockExam.grade.guide.fullMarks")}</Typography>
      </Box>

      {/* Segmented bar */}
      <Box sx={{ display: "flex", height: 20, borderRadius: rounded.xs, overflow: "hidden" }}>
        {segments.map(({ grade, widthPercent }) => (
          <Box
            key={grade}
            sx={{
              width: `${widthPercent}%`,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: GRADE_COLORS[grade].bg,
              color: GRADE_COLORS[grade].color,
              ...typography.cap12Bold,
            }}
          >
            {grade}
          </Box>
        ))}
      </Box>

      {resultPositionPercent !== undefined && (
        <Stack>
          <Box sx={{ position: "relative", height: 12 }}>
            <IconArrowShapeUp
              style={{
                fontSize: 12,
                color: primary[500],
                position: "absolute",
                left: `${resultPositionPercent}%`,
                transform: "translateX(-50%)",
              }}
            />
          </Box>
          <Box sx={{ position: "relative", height: 18 }}>
            <Typography
              noWrap
              sx={{
                ...typography.cap12Bold,
                color: primary[500],
                position: "absolute",
                left: `${resultPositionPercent}%`,
                maxWidth: "100%",
                transform: getMarkerLabelShift(resultPositionPercent),
              }}
            >
              {t("mockExam.grade.guide.resultMarker")}
            </Typography>
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
